import { Injectable } from '@angular/core';
import { HttpApiService } from '../http-api/http-api.service';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { catchError, finalize, map, Observable, of, Subscription, switchMap, tap } from 'rxjs';
import { ApiGetResponse, CurrencyConvertionInterface, DepartmentInterface, DetachmentInterface } from '../../interfaces/global.interface';
import { WaitHandler } from '../../classes/wait-handler.class';
import { PromptMessage } from '../../classes/promp-message.class';
import { Helper } from '../../classes/helper.class';
import { AccMessageDialogService } from 'src/app/accountancy/custom-components/acc-message-dialog/acc-message-dialog-service/acc-message-dialog.service';

@Injectable({
  providedIn: 'root'
})
export class GlobalApiService {

  constructor(
    private _api: HttpApiService,
    private _msg: AccMessageDialogService,
  ) { }

  public handleSingleGetError(callBack: () => void) {
    return of(null).pipe(tap(() => {
      this._msg.open({
        title: 'Error occurred.',
        message: '* The item you are looking for may not be accessible for you or is removed by the creator.',
        messageType: 'text-red-400',
        onClose: () => callBack(),
      }, true);
    }));
  }

  /**
   * `1st` - Get the upload URL to upload a file. After this you must use `putFileInServer()` for next step.
   * @param file The actual file to be uploaded
   * @param filename The actual filename to be uploaded
   * @param directory The directory where the file is saved. `"Receipts"` for Accounting. `"employeeImage"` for HRIS employee image.
   * @returns 
   */
  public getUploadFileUrl(file: File, filename: string, directory: "Receipts" | "employeeImage") {
    return this._api.hris.sendPostRequest<HttpResponse<{ presignedURL: string }>>('/get/presigned/upload', {
      fileName: `${filename}`,
      fileType: file.type,
      fileBaseDir: directory,
    },
      new HttpHeaders({ "content-type": file.type }),
    );
  }

  /**
   * `2nd` - Upload the actual file to the server. After this you can now use `getFileUrl()`.
   * @param presignedURL The URL fetched when requesting for upload URL in 1st step.
   * @param file The actual file to be uploaded.
   * @returns 
   */
  public putFileInServer(presignedURL: string, file: File) {
    return this._api.raw.sendPutRequest<HttpResponse<any>>(presignedURL, file, new HttpHeaders({ "content-type": file.type }),);
  }

  /**
   * `1st/3rd` Gets the file URL uploaded in the server. If you want to upload new file, use `getUploadFileUrl()`
   * @param filename File name uploaded on the server.
   * @param directory The directory where the file is saved. `"Receipts"` for Accounting. `"employeeImage"` for HRIS employee image.
   * @returns 
   */
  public getFileUrl(filename: string, directory: "Receipts" | "employeeImage") {
    return this._api.hris.sendGetRequest<HttpResponse<{ result: string, url: string }>>(`/get_file_presigned_url?fileName=${filename}&fileBaseDir=${directory}`);
  }

  /**
   * `Combined` Upload a file to the server
   * @param waitHandler Your WaitHandler instance.
   * @param filename File name uploaded on the server.
   * @param file The actual file to be uploaded.
   * @param directory The directory where the file is saved. `"Receipts"` for Accounting. `"employeeImage"` for HRIS employee image.
   * @returns 
   */
  public uploadFileWrapped(waitHandler: WaitHandler, fileName: string, file: File, directory: "Receipts" | "employeeImage") {
    return of(null).pipe(
      tap(() => waitHandler.newRequest()),
      switchMap(() => this.getUploadFileUrl(file, fileName, directory).pipe(
        finalize(() => waitHandler.finishRequest()),
        map(values => values.body?.presignedURL),
        switchMap(presignedURL => of(presignedURL).pipe(
          tap(() => waitHandler.newRequest()),
          switchMap(presignedURL => this.putFileInServer(presignedURL ?? '', file).pipe(
            finalize(() => waitHandler.finishRequest()),
          ))
        ))
      ))
    );
  }

  public getDepartmentList() {
    return this._api.hris.sendGetRequest<HttpResponse<any>>('/departments').pipe(
      map(values => {
        return values?.body.result as DepartmentInterface[];
      }),
    )
  }

  public getDetachmentList() {
    return this._api.hris.sendGetRequest<HttpResponse<DetachmentInterface[]>>('/detachments-list').pipe(
      map(values => {
        return values?.body;
      }),
    )
  }

  public getRegionList(Start?: number | null, Limit?: number | null, Query?: string | null, other?: any) {
    return this._api.hris2.sendGetRequest<HttpResponse<ApiGetResponse<{
      ID: number, Name: string
    }>>>(Helper.url.toParams(`/api/v1/rate-per-regions`, {
      Start, Limit, Query, ...other
    })).pipe(
      map(values => values.body),
    );
  }

  // public (PHPvalue: sconvertPhpToUsdtring | number): Observable<CurrencyConvertionInterface> {
  //   return this._api.raw.sendGetRequest<HttpResponse<CurrencyConvertionInterface>>(`https://api.frankfurter.app/latest?amount=${PHPvalue}&from=PHP&to=USD`).pipe(
  //     map(values => values?.body),
  //     map((values: any) => ({ ...values, Passedamount: values.amount } as CurrencyConvertionInterface)),
  //     catchError(() => of({ Passedamount: 0, base: "", date: "", rates: { USD: 0, } })),
  //   );
  // }

}
